import {makeAutoObservable} from "mobx";
import {uuid} from "../../../services/helpers";
import {IProduct, IProductRma, PRODUCT_STATUS_ENUM, SHOPIFY_STATUS_ENUM} from "../../../services/order-service";
import OrderStore from "./order-store";

interface IProductUIStatus {
    [key: string]: { textTranslationKey: string, class: string }
}

export const PRODUCT_STATUS_MAP: IProductUIStatus = {
    [PRODUCT_STATUS_ENUM.RETURNED]: { textTranslationKey: 'PRODUCT_STATUS_RETURNED', class: 'returned' },
    [PRODUCT_STATUS_ENUM.REJECTED]: { textTranslationKey: 'PRODUCT_STATUS_REJECTED', class: 'rejected' },
    [PRODUCT_STATUS_ENUM.VIRTUAL_GOOD]: { textTranslationKey: 'PRODUCT_STATUS_VIRTUAL_GOOD', class: 'virtual-good' },
    [PRODUCT_STATUS_ENUM.DANGEROUS_GOOD]: { textTranslationKey: 'PRODUCT_STATUS_DANGEROUS_GOOD', class: 'dangerous-good' },
    [SHOPIFY_STATUS_ENUM.UNFULFILLED]: { textTranslationKey: 'PRODUCT_STATUS_UNFULFILLED', class: 'unfulfilled' },
    [SHOPIFY_STATUS_ENUM.FULFILLED]: { textTranslationKey: 'PRODUCT_STATUS_FULFILLED', class: 'fulfilled' },
}

class OrderProductStore {
	private _productList?: IProduct[] = [];
	private _uuid: string = uuid();
	private _showAddToRma:boolean = false;

	
	constructor(private _orderStore: OrderStore, data: IProduct[]) {
		this._productList = data.map(product => ensureValidModel(product));
		makeAutoObservable(this, {}, {autoBind: true});
	}

	private get _product(): IProduct | undefined { return this._productList?.[0]; }
	private get _conversionRate(): number { return this._orderStore.conversionRate; }
    
    
    public get paymentCurrencyCode(): string { return this._orderStore.paymentCurrencyCode; }
    public get storeCurrencyCode(): string { return this._orderStore.storeCurrencyCode; }
    public get uuid(): string { return this._uuid;	}
	public get lineItemId():string | null { return this._product?.shop_line_item_id ?? null; }
	public get rma(): IProductRma | undefined { return this._product?.rma; }
	public get rmaId(): string | undefined { return this._product?.rma?.id; }
	public get isShopifyReturned():boolean { return this._product?.status === PRODUCT_STATUS_ENUM.RETURNED && !this.rma; }
	public get isRejected():boolean { return this._product?.status === PRODUCT_STATUS_ENUM.REJECTED; }
	public get imageUrl(): string { return this._product?.image.url ?? ''; }
	public get name(): string { return this._product?.name ?? ''; }
	public get quantity(): number { return this._productList?.length ?? 0; }
	public get paidAmount(): number { return this._product?.presentment_money ?? 0; }
	public get catalogPrice(): number { return (this._product?.catalog_price ?? 0) / this._conversionRate; }
	public get fulfillmentDate(): string | undefined { return this._product?.fulfilledAt; }
	public get variantTitle(): string {return this._product?.variantTitle ?? ''; }
	public get showAddToRma(): boolean { return this._showAddToRma; }
	public get discountAmount(): number { return this._product?.presentment_discount_amount ?? 0; }
    public get variantUrl(): string { return this._product?.variant_url ?? ''; }
    public get hasBxGy(): boolean {return this._product?.bxgy_group_info !== undefined; }
    public get isReturnBlockedVirtual(): boolean { return this._orderStore.isLineItemReturnBlockedVirtual(this.lineItemId); }
    public get isReturnBlockedDangerousGood(): boolean { return this._orderStore.isLineItemReturnBlockedDangerousGood(this.lineItemId); }
    public get isReturnBlocked(): boolean { return this.isReturnBlockedVirtual || this.isReturnBlockedDangerousGood; }
    public get returnBlockedStatus(): { textTranslationKey: string, class: string } | undefined {
        if (this.isReturnBlockedDangerousGood) {
            return PRODUCT_STATUS_MAP[PRODUCT_STATUS_ENUM.DANGEROUS_GOOD];
        }
        if (this.isReturnBlockedVirtual) {
            return PRODUCT_STATUS_MAP[PRODUCT_STATUS_ENUM.VIRTUAL_GOOD];
        }
        return undefined;
    }
	public get eligiblePolicyRules(): number[]|undefined { return this._orderStore.getLineItemPolicies(this._product?.shop_line_item_id); }
    public get isHidden(): boolean { return this._product?._is_hidden ?? false; }
  	public get status(): { textTranslationKey: string, class: string } {
		//if returned or rejected
		if(this._product?.status && (this._product.status === PRODUCT_STATUS_ENUM.RETURNED || this._product.status === PRODUCT_STATUS_ENUM.REJECTED)){
			return PRODUCT_STATUS_MAP[this._product.status] ?? { textTranslationKey: '', class: '' };
		}
		//else it's either fulfilled or unfulfilled
		else if (this._product?.fulfillment_status){
			return PRODUCT_STATUS_MAP[this._product?.fulfillment_status] ?? { textTranslationKey: '', class: '' };
		}
		//fallback
		else {
            return { textTranslationKey: '', class: '' };
        }
	}
    public get fulfillmentStatus(): { textTranslationKey: string, class: string } {
        if (this._product?.fulfillment_status) {
            return PRODUCT_STATUS_MAP[this._product.fulfillment_status] ?? { textTranslationKey: '', class: '' };
        }
        return { textTranslationKey: '', class: '' };
    }
    public get isCustomItem(): boolean { return !this.lineItemId; }
    
    public compareAtPrice(): number | null {
        const compareAtPrice = this._product?.discount_compared_at_price_amount_original ?? this._product?.discount_compared_at_price_amount_current;
        if(!compareAtPrice){
            return null;
        }
        return compareAtPrice / this._conversionRate;
    }
    
    
    public isFulfilled(): boolean {return this._product?.fulfillment_status ==='FULFILLED'}

	
	public setShowAddToRma(showAddToRma: boolean){
		this._showAddToRma = showAddToRma;
	}
 
}

function ensureValidModel(model: IProduct){
    return {...model,
        presentment_money: Number(model.presentment_money),
        discount_amount: Number(model.discount_amount),
        catalog_price: Number(model.catalog_price)
    }
}
export default OrderProductStore;